import { createSlice } from '@reduxjs/toolkit'
import clientSubscriptionData from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Client/clientSubscriptionData'
import { parseTab } from '@/shared/tools/activeUtil'
import addressSubscriptionData from './Address/addressSubscriptionData'

const initialState = {
  client: { ...clientSubscriptionData },
  addresses: {
    new: {},
    config: {
      addressList: [],
      addressIsLoading: false,
      caption: null,
      errorText: null,
      errors: {},
      addressIsSaving: false,
      showAddressFields: false,
      showEnergyLandscape: false,
    },
  },
  contracts: {},
  comments: [],
  additionalSalesConfig: {
    salesConfigLoaded: false,
    existingSalesLoaded: false,
    groupConfig: {},
    sales: {},
  },
  additionalSales: {},
  openedContracts: [],
  config: {
    activeTab: 'client',
    addContractModal: false,
    createContractSelectedType: null,
    activeAddress: null,
    contactId: null,
    isImportingContactFromZoho: false,
  },
  communicationBoard: {
    loading: false,
    error: null,
    sections: [],
    availableSections: [],
  },
  pdlSearchData: {
    address: {},
    searchPodResponses: [],
    searchPodResponsesOriginal: [],
    viewPage: 'search',
    occupant: '',
    serialNumber: '',
    complement: '',
    building: '',
    locality: '',
    contractType: 'part',
    matricule: '',
    pdlSelected: '',
    isOpen: false,
    isLoading: false,
    page: 1,
    errorMessage: null,
    podSearchDataId: null,
  },
  pceSearchData: {
    address: {},
    searchPodResponses: [],
    searchPodResponsesOriginal: [],
    viewPage: 'search',
    occupant: '',
    serialNumber: '',
    pceSelected: '',
    isOpen: false,
    isLoading: false,
    page: 1,
    errorMessage: null,
    podSearchDataId: null,
  },
  scheduledCallback: {
    loading: false,
    success: false,
    message: null,
    error: null,
    lastCreatedAt: null,
    googleCalendarUrl: null,
    form: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      scheduledDate: '',
      hour: '',
      minutes: '',
      assigned: 'self',
      isValid: false,
    },
    upcoming: {
      isLoading: false,
      loadedForZohoId: null,
      items: [],
      error: null,
    },
  },
  clubSelectra: {
    loading: false,
    success: false,
    message: null,
    error: null,
  },
  gryvChecklist: {
    checkedTitles: {
      identity: false,
      presentation: false,
      visitPurpose: false,
      recap: false,
    },
    expanded: {
      identity: false,
      presentation: false,
      visitPurpose: false,
      recap: false,
    },
  },
}

const initializeContractDataHandler = (state, action) => {
  state.client = action.payload.client
  state.addresses = {
    ...state.addresses,
    ...action.payload.addresses,
  }
  state.config.contactId = action.payload.contactId
  state.contracts = action.payload.contracts
  state.comments = action.payload.comments
}

const importContractFromZohoDataHandler = (state, action) => {
  const { client, addresses } = action.payload
  const originalPreferences = state.client.preferences || {}
  const originalConfig = state.client.config || {}

  if (client.preferences) {
    delete client.preferences.dataCollectionAuth
  }

  state.client = {
    ...state.client,
    ...client,
    preferences: {
      ...originalPreferences,
      ...client.preferences,
    },
    config: {
      ...originalConfig,
    },
  }

  addresses.forEach(address => {
    state.addresses[address.id] = address
  })
}

const toggleGroupVisibilityHandler = (state, action) => {
  const group = action.payload
  state.additionalSalesConfig.groupConfig[group].visible = !state.additionalSalesConfig.groupConfig[group].visible
}

const updateAdditionalSalesConfigHandler = (state, action) => {
  state.additionalSalesConfig = {
    ...state.additionalSalesConfig,
    ...action.payload,
  }
}

// example of dispatch: dispatch(updateAdditionalSalesData({data: {test: 'test'}, name: 'voltalis'}))
const updateAdditionalSalesDataHandler = (state, action) => {
  // really weird stuff to don't update from status update for Assistance if it's not the same address currently selected
  // then update in all assistances sales array the needed data
  const payload = action.payload.data || {}
  if (action.payload.name === 'leadAssistance' && payload.infoAttributes?.addressId?.length > 0) {
    const currentData = state.additionalSales['leadAssistance'] || {}
    if (currentData?.addressId?.toString() !== payload.infoAttributes?.addressId?.toString()) {
      const allAssistanceSales = state.additionalSales.allAssistancesSales || []
      const assistanceIndex = allAssistanceSales.findIndex(
        assistance => assistance?.addressId?.toString() === payload.infoAttributes?.addressId?.toString(),
      )
      if (assistanceIndex !== -1) {
        state.additionalSales.allAssistancesSales[assistanceIndex] = {
          ...state.additionalSales.allAssistancesSales[assistanceIndex],
          ...payload,
        }
      } else {
        state.additionalSales.allAssistancesSales.push({
          ...payload,
          ...payload.infoAttributes,
        })
      }
      return
    }
  }
  state.additionalSales[action.payload.name] = {
    ...state.additionalSales[action.payload.name],
    ...action.payload.data,
  }
}

const initAdditionalSalesHandler = (state, action) => {
  state.additionalSales = {
    ...action.payload,
  }
}

// dispatch(updateClientDataHandler({data: {firstName: 'Tomas'}, name: 'personal'}))
const updateClientDataHandler = (state, action) => {
  state.client[action.payload.name] = {
    ...state.client[action.payload.name],
    ...action.payload.data,
  }
}

const addClientErrorHandler = (state, action) => {
  state.client.config.errors = {
    ...state.client.config.errors,
    [action.payload.key]: [action.payload.message],
  }
}

const removeClientErrorHandler = (state, action) => {
  const newErrorState = { ...state.client.config.errors }
  if (newErrorState[action.payload]) {
    delete newErrorState[action.payload]
  }
  state.client.config.errors = newErrorState
}

const updateAddressDataHandler = (state, action) => {
  const addressKey = action.payload.id ?? 'new'
  state.addresses[addressKey] = {
    ...state.addresses[addressKey],
    ...action.payload.data,
  }
}

const updateElectricityMeterAddressDataHandler = (state, action) => {
  const addressKey = action.payload.id ?? 'new'
  state.addresses[addressKey].electricityMeter = {
    ...(state.addresses[addressKey].electricityMeter || {}),
    ...action.payload.data,
  }
}

const updateGasMeterAddressDataHandler = (state, action) => {
  const addressKey = action.payload.id ?? 'new'
  state.addresses[addressKey].gasMeter = {
    ...(state.addresses[addressKey].gasMeter || {}),
    ...action.payload.data,
  }
}

const updateAddressesConfigDataHandler = (state, action) => {
  state.addresses.config = {
    ...state.addresses.config,
    ...action.payload,
  }
}

const addAddressErrorHandler = (state, action) => {
  const previousAddressErrorsState = state.addresses.config.errors[action.payload.id] ?? {}
  state.addresses.config.errors = {
    ...state.addresses.config.errors,
    [action.payload.id]: { ...previousAddressErrorsState, ...action.payload.data },
  }
}

const removeAddressErrorHandler = (state, action) => {
  const newState = { ...state.addresses.config.errors }

  if (newState[action.payload.id]) {
    delete newState[action.payload.id][action.payload.key]
  }
  state.addresses.config.errors = newState
}

const resetAddressDataHandler = (state, action) => {
  const addressKey = action.payload.id ?? 'new'
  const contactId = state.addresses[addressKey]?.contactId || null
  const addressId = state.addresses[addressKey]?.id || 0
  state.addresses[addressKey] = { ...addressSubscriptionData, id: addressId, contactId }
}

const updateContractCharacteristicsDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].characteristics = {
    ...(state.contracts[action.payload.id].characteristics || {}),
    ...action.payload.data,
  }
}

const updateContractDefaultDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id] = {
    ...(state.contracts[action.payload.id] || {}),
    ...action.payload.data,
  }
}

const updateContractConsumptionDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].consumption = {
    ...(state.contracts[action.payload.id].consumption || {}),
    ...action.payload.data,
    prefilled: {
      ...(state.contracts[action.payload.id].consumption?.prefilled || {}),
      ...action.payload.data.prefilled,
    },
  }
}

const updateContractPropaneDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].propane = {
    ...(state.contracts[action.payload.id].propane || {}),
    ...action.payload.data,
  }
}

const updateContractElecDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].elec = {
    ...(state.contracts[action.payload.id].elec || {}),
    ...action.payload.data,
  }
}

const addContractErrorsHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].errors = {
    ...(state.contracts[action.payload.id].errors || {}),
    ...action.payload.data,
  }
}

const resetContractErrorsHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].errors = {}
}

const removeErrorFromContractHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  const currentErrors = state.contracts[action.payload.id].errors || {}
  if (currentErrors[action.payload.data]) {
    delete currentErrors[action.payload.data]
    state.contracts[action.payload.id].errors = currentErrors
  }
}

const updateContractGasDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].gas = {
    ...(state.contracts[action.payload.id].gas || {}),
    ...action.payload.data,
  }
}

const updateContractDetailsDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].contractDetails = {
    ...(state.contracts[action.payload.id].contractDetails || {}),
    ...action.payload.data,
  }
}

const updateContractBillingDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].billing = {
    ...(state.contracts[action.payload.id].billing || {}),
    ...action.payload.data,
  }
}

const updateContractAddressIdHandler = (state, action) => {
  state.contracts[action.payload.id].addressId = action.payload.addressId
}

const updateContractConfigDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].config = {
    ...(state.contracts[action.payload.id].config || {}),
    ...action.payload.data,
  }
}

const updateActiveTabHandler = (state, action) => {
  state.config.activeTab = action.payload
}

const updateActiveAddressHandler = (state, action) => {
  state.config.activeAddress = action.payload
}

const updateConfigHandler = (state, action) => {
  state.config = {
    ...state.config,
    ...action.payload,
  }
}

const updatePdlSearchDataHandler = (state, action) => {
  state.pdlSearchData = {
    ...state.pdlSearchData,
    ...action.payload,
  }
}

const updatePceSearchDataHandler = (state, action) => {
  state.pceSearchData = {
    ...state.pceSearchData,
    ...action.payload,
  }
}

const addContractToOpenedContractsHandler = (state, action) => {
  const keyName = action.payload.id + '-' + action.payload.type
  state.contracts[action.payload.id] = action.payload
  if (!state.openedContracts.includes(action.payload.id)) {
    state.openedContracts.push(action.payload.id)
  }
  state.config.activeTab = keyName
}

const removeContractFromOpenedContractsHandler = (state, action) => {
  state.openedContracts = state.openedContracts.filter(contractId => contractId !== action.payload.id)
  const { id } = parseTab(state.config.activeTab)

  if (id === action.payload.id) {
    state.config.activeTab = 'client'
  }
}

const updateCommunicationBoardDataHandler = (state, action) => {
  state.communicationBoard = {
    ...state.communicationBoard,
    ...action.payload,
  }
}

const setCommunicationBoardErrorHandler = (state, action) => {
  state.communicationBoard.error = action.payload
}

const updateScheduledCallbackHandler = (state, action) => {
  const payload = action.payload || {}
  const { form, ...rest } = payload

  state.scheduledCallback = {
    ...state.scheduledCallback,
    ...rest,
  }
  if (form) {
    state.scheduledCallback.form = {
      ...state.scheduledCallback.form,
      ...form,
    }
  }
}

const updateClubSelectraHandler = (state, action) => {
  state.clubSelectra = {
    ...state.clubSelectra,
    ...action.payload,
  }
}

const addCommentToContactHandler = (state, action) => {
  state.comments.push({ ...action.payload })
}

const updateGryvChecklistCheckedTitleHandler = (state, action) => {
  const { key, checked } = action.payload
  state.gryvChecklist.checkedTitles[key] = checked
}

const toggleGryvChecklistExpandedHandler = (state, action) => {
  const key = action.payload
  state.gryvChecklist.expanded[key] = !state.gryvChecklist.expanded[key]
}

export const SubscriptionFormSlice = createSlice({
  name: 'formV2',
  initialState,
  reducers: {
    initializeContractData: initializeContractDataHandler,
    importContractFromZohoData: importContractFromZohoDataHandler,
    updateClientData: updateClientDataHandler,
    updateAddressData: updateAddressDataHandler,
    updateElectricityMeterAddressData: updateElectricityMeterAddressDataHandler,
    updateGasMeterAddressData: updateGasMeterAddressDataHandler,
    resetAddressData: resetAddressDataHandler,
    updateAddressesConfigData: updateAddressesConfigDataHandler,
    addAddressError: addAddressErrorHandler,
    removeAddressError: removeAddressErrorHandler,
    updateContractCharacteristicsData: updateContractCharacteristicsDataHandler,
    updateContractConsumptionData: updateContractConsumptionDataHandler,
    updateContractElecData: updateContractElecDataHandler,
    updateContractGasData: updateContractGasDataHandler,
    updateContractPropaneData: updateContractPropaneDataHandler,
    updateContractDetailsData: updateContractDetailsDataHandler,
    updateContractBillingData: updateContractBillingDataHandler,
    updateContractAddressId: updateContractAddressIdHandler,
    addContractToOpenedContracts: addContractToOpenedContractsHandler,
    updateContractConfigData: updateContractConfigDataHandler,
    removeContractFromOpenedContracts: removeContractFromOpenedContractsHandler,
    updateConfig: updateConfigHandler,
    updateActiveTab: updateActiveTabHandler,
    updateActiveAddress: updateActiveAddressHandler,
    addClientError: addClientErrorHandler,
    removeClientError: removeClientErrorHandler,
    addContractErrors: addContractErrorsHandler,
    resetContractErrors: resetContractErrorsHandler,
    removeErrorFromContract: removeErrorFromContractHandler,
    updateContractDefaultData: updateContractDefaultDataHandler,
    toggleGroupVisibility: toggleGroupVisibilityHandler,
    updateAdditionalSalesConfig: updateAdditionalSalesConfigHandler,
    updateAdditionalSalesData: updateAdditionalSalesDataHandler,
    initAdditionalSales: initAdditionalSalesHandler,
    updateCommunicationBoardData: updateCommunicationBoardDataHandler,
    setCommunicationBoardError: setCommunicationBoardErrorHandler,
    updatePdlSearchData: updatePdlSearchDataHandler,
    updatePceSearchData: updatePceSearchDataHandler,
    updateScheduledCallback: updateScheduledCallbackHandler,
    updateClubSelectra: updateClubSelectraHandler,
    addCommentToContact: addCommentToContactHandler,
    updateGryvChecklistCheckedTitle: updateGryvChecklistCheckedTitleHandler,
    toggleGryvChecklistExpanded: toggleGryvChecklistExpandedHandler,
  },
})

export const {
  initializeContractData,
  importContractFromZohoData,
  updateClientData,
  addClientError,
  removeClientError,
  updateAddressData,
  updateElectricityMeterAddressData,
  updateGasMeterAddressData,
  resetAddressData,
  updateAddressesConfigData,
  addAddressError,
  removeAddressError,
  updateContractCharacteristicsData,
  updateContractConsumptionData,
  updateContractElecData,
  updateContractGasData,
  updateContractPropaneData,
  updateContractDetailsData,
  updateContractBillingData,
  updateContractAddressId,
  updateContractConfigData,
  addContractToOpenedContracts,
  removeContractFromOpenedContracts,
  updateConfig,
  updateActiveTab,
  updateActiveAddress,
  addScript,
  removeScript,
  addContractErrors,
  removeErrorFromContract,
  resetContractErrors,
  updateContractDefaultData,
  toggleGroupVisibility,
  updateAdditionalSalesConfig,
  updateAdditionalSalesData,
  initAdditionalSales,
  updateCommunicationBoardData,
  setCommunicationBoardError,
  updatePdlSearchData,
  updatePceSearchData,
  updateScheduledCallback,
  updateClubSelectra,
  addCommentToContact,
  updateGryvChecklistCheckedTitle,
  toggleGryvChecklistExpanded,
} = SubscriptionFormSlice.actions

export default SubscriptionFormSlice.reducer
